import { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../constants/environment';
import type { CardSearchResponse, RiftboundCard } from '../types';
import { emptyFilter } from '../types';
import { buildCardSearchParams } from '../utils/cardUtils';

const WALL_CACHE_KEY = 'riftbound_wall_cards_v1';
const WALL_CACHE_TTL_MS = 6 * 60 * 60 * 1000;

interface WallCache {
  cards: RiftboundCard[];
  ts: number;
}

function shuffle<T>(items: T[]): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function readWallCache(): RiftboundCard[] | null {
  try {
    const raw = localStorage.getItem(WALL_CACHE_KEY);
    if (!raw) return null;
    const { cards, ts } = JSON.parse(raw) as WallCache;
    if (Date.now() - ts > WALL_CACHE_TTL_MS) return null;
    return cards;
  } catch {
    return null;
  }
}

function writeWallCache(cards: RiftboundCard[]): void {
  try {
    const payload: WallCache = { cards, ts: Date.now() };
    localStorage.setItem(WALL_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // ignore quota / private-browsing errors
  }
}

function deferDecorativeFetch(callback: () => void): () => void {
  const win = window as Window & {
    requestIdleCallback?: (cb: () => void, options?: { timeout: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

  if (typeof win.requestIdleCallback === 'function') {
    const handle = win.requestIdleCallback(callback, { timeout: 1500 });
    return () => win.cancelIdleCallback?.(handle);
  }

  const handle = window.setTimeout(callback, 400);
  return () => window.clearTimeout(handle);
}

/** Card pool + localStorage cache backing `CardWall` (shared with Home). */
export function useCardWallPool(): RiftboundCard[] {
  const [sphereCards, setSphereCards] = useState<RiftboundCard[]>(() => readWallCache() ?? []);

  useEffect(() => {
    const cached = readWallCache();
    if (cached) return;

    let cancelled = false;
    const cancelDeferredFetch = deferDecorativeFetch(() => {
      void (async () => {
        try {
          const params = buildCardSearchParams(emptyFilter, '', 'set-newest', 1, 80);
          const res = await fetch(`${getApiBaseUrl()}/api/cards?${params.toString()}`);
          if (!res.ok || cancelled) return;
          const data = (await res.json()) as CardSearchResponse;
          const cards = data.cards ?? [];
          const withArt = cards.filter((c) => !!(c.thumbnailUrl || c.imageUrl || '').trim());
          const pool = withArt.length >= 12 ? withArt : cards;
          const shuffled = shuffle(pool).slice(0, 72);
          if (!cancelled) {
            writeWallCache(shuffled);
            setSphereCards(shuffled);
          }
        } catch {
          // fail silently — wall simply won't show
        }
      })();
    });

    return () => {
      cancelled = true;
      cancelDeferredFetch();
    };
  }, []);

  return sphereCards;
}
